// GroupManagementModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CloseIcon,
  SearchIcon,
  CheckIcon,
  PhotoIcon,
  UserPlusIcon,
  UserMinusIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "./ChatIcons"; // Adjust import path as needed
import fetchApi from '../../../../../Lib/api';
import { IconUserMinus, IconUserPlus } from '@tabler/icons-react';

const GroupManagementModal = ({
  show,
  onClose,
  activeConversation,
  currentUserId,
  isCurrentUserAdminOrCreator,
  onAddParticipant, // Function from ChatInterface to add
  onRemoveParticipant, // Function from ChatInterface to remove
  onUpdateGroupInfo, // Function to update group name/avatar
}) => {
  // Tab state - similar to ComposeModal
  const [activeTab, setActiveTab] = useState('members'); // 'members', 'edit', 'add'
  
  // Edit group info state
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupAvatarPreview, setEditGroupAvatarPreview] = useState(null);
  const [editGroupAvatarFile, setEditGroupAvatarFile] = useState(null);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);
  
  // Search state for adding members
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState(new Set());

  // Loading states
  const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);
  const [error, setError] = useState(null);

  // Memoized list of current participants for display
  const currentParticipants = useMemo(() => {
    return activeConversation?.participants || [];
  }, [activeConversation]);

  // Initialize edit form when conversation changes
  useEffect(() => {
    if (activeConversation) {
      setEditGroupName(activeConversation.title || '');
      setEditGroupAvatarPreview(activeConversation.avatar || null);
    }
  }, [activeConversation]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setActiveTab('members');
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUsersToAdd(new Set());
      setSearchError(null);
      setIsLoadingSearch(false);
      setIsRemovingParticipant(false);
      setEditGroupAvatarFile(null);
      setError(null);
    }
  }, [show]);

  // Debounced search for users NOT in the current conversation
  useEffect(() => {
    if (!show || !activeConversation?.id || activeTab !== 'add') {
      setSearchResults([]);
      return;
    }

    const term = searchTerm.trim();
    const debounceTimer = setTimeout(() => {
      if (term.length > 0) {
        setIsLoadingSearch(true);
        setSearchError(null);

        const url = term
        ? `/conversations/search_users?search=${encodeURIComponent(searchTerm)}`
        : `/conversations/search_users`;

        fetchApi(url, 'GET')
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(users => {
            const filteredUsers = users.filter(user =>
              !currentParticipants.some(p => String(p.id) === String(user.id))
            );
            setSearchResults(filteredUsers);
          })
          .catch(error => {
            console.error("Search for available users failed:", error);
            setSearchError("Failed to search for users. Please try again.");
            setSearchResults([]);
          })
          .finally(() => {
            setIsLoadingSearch(false);
          });
      } else {
        setSearchResults([]);
        setSearchError(null);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, activeConversation, currentParticipants, show, activeTab]);

  // Handle avatar file change
  const handleAvatarChange = useCallback((event) => {
    const file = event.target.files?.[0];
    setEditGroupAvatarFile(file || null);
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditGroupAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Handle updating group info
  const handleUpdateGroupInfo = useCallback(async () => {
    if (!editGroupName.trim()) return;
    
    setIsUpdatingGroup(true);
    setError(null);
    
    try {
      const success = await onUpdateGroupInfo(activeConversation.id, {
        name: editGroupName.trim(),
        avatarFile: editGroupAvatarFile
      });
      
      if (success) {
        setActiveTab('members'); // Go back to members tab
        setEditGroupAvatarFile(null);
      } else {
        setError("Failed to update group information");
      }
    } catch (err) {
      setError("Failed to update group information");
    } finally {
      setIsUpdatingGroup(false);
    }
  }, [editGroupName, editGroupAvatarFile, activeConversation?.id, onUpdateGroupInfo]);

  // Handle toggling user selection for adding
  const handleToggleUserSelection = useCallback((userId) => {
    setSelectedUsersToAdd(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  // Handle adding selected users
  const handleAddSelectedUsers = useCallback(async () => {
    const usersToAdd = Array.from(selectedUsersToAdd);
    if (usersToAdd.length === 0) return;

    setError(null);
    let allSuccess = true;
    
    for (const userId of usersToAdd) {
      const success = await onAddParticipant(activeConversation.id, userId);
      if (!success) {
        allSuccess = false;
      }
    }

    if (allSuccess) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUsersToAdd(new Set());
      setActiveTab('members'); // Go back to members tab
    } else {
      setError("Some users could not be added");
    }
  }, [selectedUsersToAdd, activeConversation?.id, onAddParticipant]);

  // Handle removing participant
  const handleRemoveParticipant = useCallback(async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from this group?`)) {
      return;
    }
    
    setIsRemovingParticipant(true);
    setError(null);
    
    const success = await onRemoveParticipant(activeConversation.id, userId);
    if (!success) {
      setError("Failed to remove participant");
    }
    
    setIsRemovingParticipant(false);
  }, [activeConversation?.id, onRemoveParticipant]);

  if (!show || !activeConversation || activeConversation.type !== 'group') {
    return null;
  }

  return (
    <div
      role="dialog"
      className="relative z-20"
      aria-labelledby="modal-title"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
        onClick={onClose}
      ></div>
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-10 h-full overflow-y-auto">
        <div className="h-full flex items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg pt-6 pb-0 shadow-xl transform transition-all sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4 px-5">
              <p
                className="text-black opacity-80 dark:text-white text-lg font-semibold leading-4 tracking-[.01rem]"
                id="modal-title"
                tabIndex="0"
              >
                Group Settings
              </p>
              <button
                tabIndex="0"
                onClick={onClose}
                aria-label="Close group settings modal"
                className="group p-1 rounded-full flex justify-center items-center ease-out text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-5 pb-4">
              <div className="flex content-stretch gap-1 p-1 rounded-md bg-gray-100 dark:bg-gray-700">
                <button
                  onClick={() => setActiveTab('members')}
                  className={`py-2 px-3 flex-auto rounded text-sm outline-none focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-all duration-200 ${
                    activeTab === 'members'
                      ? "bg-indigo-500 text-white shadow"
                      : "text-black opacity-60 dark:text-white dark:opacity-70 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  Members
                </button>
                {isCurrentUserAdminOrCreator && (
                  <>
                    <button
                      onClick={() => setActiveTab('edit')}
                      className={`py-2 px-3 flex-auto rounded text-sm outline-none focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-all duration-200 ${
                        activeTab === 'edit'
                          ? "bg-indigo-500 text-white shadow"
                          : "text-black opacity-60 dark:text-white dark:opacity-70 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setActiveTab('add')}
                      className={`py-2 px-3 flex-auto rounded text-sm outline-none focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-all duration-200 ${
                        activeTab === 'add'
                          ? "bg-indigo-500 text-white shadow"
                          : "text-black opacity-60 dark:text-white dark:opacity-70 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      Add
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mx-5 mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Loading Overlay for removing participants */}
            {isRemovingParticipant && (
              <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 flex items-center justify-center z-10 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            )}

            {/* Content Area based on Tab */}
            <div className="overflow-x-hidden">
              {/* Members Tab */}
              {activeTab === 'members' && (
                <>
                  <div className="px-5 mb-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-black opacity-70 dark:text-white dark:opacity-80">
                        Members ({currentParticipants.length})
                      </h3>
                    </div>
                  </div>

                  <div
                    className="scrollbox overflow-y-auto max-h-[20rem] mb-5 px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent"
                    tabIndex="0"
                  >
                    {currentParticipants.map(participant => (
                      <div
                        key={participant.id}
                        className="w-full p-3 flex items-center text-left transition duration-200 ease-out outline-none rounded-md"
                      >
                        <div className="mr-4 flex-shrink-0">
                          <div
                            className="w-8 h-8 rounded-full bg-cover bg-center bg-gray-300"
                            style={{
                              backgroundImage: participant.avatar ? `url("${participant.avatar}")` : 'none',
                            }}
                          >
                            {!participant.avatar && (
                              <div className="w-full h-full rounded-full bg-indigo-500 flex items-center justify-center">
                                <span className="text-white text-xs font-semibold">
                                  {participant.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="w-full flex flex-col items-start overflow-hidden">
                          <p className="outline-none text-sm text-black opacity-70 dark:text-white dark:opacity-80 font-semibold leading-4 tracking-[.01rem] truncate">
                            {participant.name}
                            {String(participant.id) === String(currentUserId) && ' (You)'}
                            {String(participant.id) === String(activeConversation.creator_id) && ' (Creator)'}
                          </p>
                          <p className="mt-1 outline-none text-xs text-black opacity-50 dark:text-white dark:opacity-60 font-normal leading-4 tracking-[.01rem] truncate">
                            {String(participant.id) === String(activeConversation.creator_id) ? 'Group Creator' : 'Member'}
                          </p>
                        </div>
                        {/* Remove button - only for admin/creator and not self */}
                        {isCurrentUserAdminOrCreator && String(participant.id) !== String(currentUserId) && (
                          <div className="ml-auto pl-3 flex items-center flex-shrink-0">
                            <button
                              onClick={() => handleRemoveParticipant(participant.id, participant.name)}
                              className="p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors duration-200"
                              title="Remove member"
                            >
                              <IconUserMinus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {currentParticipants.length === 0 && (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                        No members found.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Edit Tab */}
              {activeTab === 'edit' && isCurrentUserAdminOrCreator && (
                <div className="px-5">
                  {/* Group Name Input */}
                  <div className="mb-5">
                    <div className="flex justify-start">
                      <label htmlFor="group-name" className="mb-2 block">
                        <span className="w-13 text-sm text-black opacity-60 dark:text-white dark:opacity-70 font-semibold leading-4 tracking-[.01rem]">
                          Group Name
                        </span>
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        id="group-name"
                        type="text"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        className="max-w-full w-full h-10 p-2 rounded-md content-center placeholder:text-black placeholder:opacity-40 text-opacity-70 dark:placeholder:text-white dark:placeholder:opacity-70 focus:outline-none transition duration-200 ease-out text-black bg-gray-50 dark:text-white border dark:border-gray-600 dark:bg-gray-700 focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-800"
                        placeholder="Group name"
                      />
                    </div>
                  </div>

                  {/* Avatar Upload */}
                  <div className="mb-5">
                    <div className="flex flex-col">
                      <div className="flex justify-start">
                        <label className="mb-2 block">
                          <span className="w-13 text-black text-opacity-60 dark:text-white dark:text-opacity-70 text-sm font-semibold leading-4 tracking-[.01rem]">
                            Group Avatar
                          </span>
                        </label>
                      </div>
                      <label
                        tabIndex="0"
                        className="cursor-pointer w-full min-h-[6.25rem] border border-dashed rounded-sm p-4 border-gray-300 dark:border-gray-500 flex justify-center items-center hover:bg-gray-100/50 focus-within:bg-gray-100/50 outline-none focus:outline-none duration-200 transition-all bg-gray-50 dark:bg-gray-700/70 dark:hover:bg-gray-700 dark:focus-within:bg-gray-700"
                      >
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleAvatarChange}
                        />
                        <div className="text-center">
                          {editGroupAvatarPreview ? (
                            <img
                              src={editGroupAvatarPreview}
                              alt="Avatar Preview"
                              className="h-16 w-16 rounded-full object-cover mx-auto mb-2"
                            />
                          ) : (
                            <PhotoIcon className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-1" />
                          )}
                          <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] text-black opacity-40 dark:text-white dark:text-opacity-70">
                            {editGroupAvatarPreview ? "Click to change" : "Choose a file or drop it here"}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            PNG, JPG, GIF up to 2MB
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="flex justify-end pb-6">
                    <button
                      tabIndex="0"
                      onClick={handleUpdateGroupInfo}
                      disabled={!editGroupName.trim() || isUpdatingGroup}
                      className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none dark:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdatingGroup ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          <span className="text-sm font-semibold leading-4 tracking-[.01rem]">Updating...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm font-semibold leading-4 tracking-[.01rem]">Save Changes</span>
                          <CheckIcon className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Add Members Tab */}
              {activeTab === 'add' && isCurrentUserAdminOrCreator && (
                <>
                  {/* Search */}
                  <div className="mx-5 mb-4">
                    <div className="relative">
                      <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none">
                        <SearchIcon className="w-5 h-5 stroke-2 text-black opacity-40 dark:text-white dark:opacity-70" />
                      </i>
                      <input
                        type="text"
                        placeholder="Search users to add..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-9 py-2 pl-10 pr-4 border outline-none rounded-md text-sm text-black dark:text-white dark:opacity-70 placeholder:text-black placeholder:opacity-40 dark:placeholder:text-white dark:placeholder:opacity-70 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 duration-200 transition ease-out text-opacity-70 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-800"
                      />
                      {isLoadingSearch && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Error */}
                  {searchError && (
                    <div className="mx-5 mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-700 dark:text-red-300">{searchError}</p>
                    </div>
                  )}

                  {/* User List with Checkboxes */}
                  <div
                    className="scrollbox overflow-y-auto max-h-[15rem] mb-5 px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent"
                    tabIndex="0"
                  >
                    {searchResults.length === 0 && searchTerm.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                        Start typing to search for users to add
                      </p>
                    ) : searchResults.length === 0 && searchTerm.length > 0 && !isLoadingSearch ? (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                        No users found
                      </p>
                    ) : (
                      searchResults.map(user => (
                        <div
                          key={user.id}
                          className="w-full p-3 flex items-center text-left transition duration-200 ease-out outline-none hover:bg-indigo-50 active:bg-indigo-100 focus:bg-indigo-50 dark:hover:bg-gray-700 dark:focus:bg-gray-700 dark:active:bg-gray-600 rounded-md cursor-pointer"
                          onClick={() => handleToggleUserSelection(user.id)}
                        >
                          <div className="mr-4 flex-shrink-0">
                            <div
                              className="w-7 h-7 rounded-full bg-cover bg-center bg-gray-300"
                              style={{
                                backgroundImage: user.avatar ? `url("${user.avatar}")` : 'none',
                              }}
                            >
                              {!user.avatar && (
                                <div className="w-full h-full rounded-full bg-indigo-500 flex items-center justify-center">
                                  <span className="text-white text-xs font-semibold">
                                    {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="w-full flex flex-col items-start overflow-hidden">
                            <p className="outline-none text-sm text-black opacity-70 dark:text-white dark:opacity-80 font-semibold leading-4 tracking-[.01rem] truncate">
                              {user.name}
                            </p>
                            <p className="mt-1 outline-none text-xs text-black opacity-50 dark:text-white dark:opacity-60 font-normal leading-4 tracking-[.01rem] truncate">
                              {user.email || 'User'}
                            </p>
                          </div>
                          <div className="ml-auto pl-3 flex items-center flex-shrink-0">
                            <div className="relative flex items-center">
                              <input
                                className="h-5 w-5 appearance-none relative cursor-pointer outline-none rounded border-2 border-gray-400 dark:border-gray-500 transition-all duration-200 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-800"
                                type="checkbox"
                                checked={selectedUsersToAdd.has(user.id)}
                                onChange={() => handleToggleUserSelection(user.id)}
                                aria-labelledby={`user-name-${user.id}`}
                              />
                              <CheckIcon
                                strokeWidth={3}
                                className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white transition-opacity duration-200 pointer-events-none ${
                                  selectedUsersToAdd.has(user.id) ? "opacity-100" : "opacity-0"
                                }`}
                              />
                            </div>
                          </div>
                          <span id={`user-name-${user.id}`} className="sr-only">
                            {user.name}
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Button */}
                  <div className="flex justify-end items-center px-5 mt-5 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      tabIndex="0"
                      onClick={handleAddSelectedUsers}
                      disabled={selectedUsersToAdd.size === 0}
                      className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none dark:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm font-semibold leading-4 tracking-[.01rem]">
                        Add Selected ({selectedUsersToAdd.size})
                      </span>
                      <IconUserPlus className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupManagementModal;